import type { Questionnaire } from '@medplum/fhirtypes';
import { hasSignatureRequired } from '../template-setup/questionnaire';
import { isCoordinationOfCareRoiTemplate } from './coordinationOfCareRoi';
import { isClientShareRestrictedTemplate } from '../clientShareRestrictedTemplates';

export type ChartDocumentSaveAction =
  | 'save'
  | 'save-and-sign'
  | 'save-sign-send'
  | 'save-sign-request-client-signature'
  | 'save-send-request-client-signature';

/** Send to client for signature without requiring a provider signature first. */
export const SEND_FOR_CLIENT_SIGNATURE_ACTION: ChartDocumentSaveAction =
  'save-send-request-client-signature';

export const CHART_DOCUMENT_SAVE_ACTIONS: readonly {
  action: ChartDocumentSaveAction;
  label: string;
}[] = [
  { action: 'save', label: 'Save' },
  { action: 'save-and-sign', label: 'Save and Sign' },
  { action: 'save-sign-send', label: 'Save, Sign, and Send to Client' },
  {
    action: 'save-sign-request-client-signature',
    label: 'Save, Sign, and Request Client Signature',
  },
  {
    action: SEND_FOR_CLIENT_SIGNATURE_ACTION,
    label: 'Save and Send for Client Signature',
  },
];

export function saveActionNeedsSignature(action: ChartDocumentSaveAction): boolean {
  return action !== 'save' && action !== SEND_FOR_CLIENT_SIGNATURE_ACTION;
}

export function saveActionSendsToClient(action: ChartDocumentSaveAction): boolean {
  return action === 'save-sign-send'
    || action === 'save-sign-request-client-signature'
    || action === SEND_FOR_CLIENT_SIGNATURE_ACTION;
}

export function sendToClientConfirmCopy(
  action: ChartDocumentSaveAction,
  documentTitle: string,
  clientName: string,
): { title: string; body: string } {
  if (action === 'save-sign-request-client-signature' || action === SEND_FOR_CLIENT_SIGNATURE_ACTION) {
    return {
      title: 'Send for client signature?',
      body: `This will send "${documentTitle}" to ${clientName} in the client portal for review and signature. They will receive a notification.`,
    };
  }
  return {
    title: 'Send to client?',
    body: `This will send "${documentTitle}" to ${clientName} in the client portal to complete. They will receive a notification.`,
  };
}

export function signSettingsForSaveAction(action: ChartDocumentSaveAction): {
  providerSignRequired: boolean;
  clientSignRequired: boolean;
} {
  switch (action) {
    case 'save':
      return { providerSignRequired: false, clientSignRequired: false };
    case 'save-and-sign':
      return { providerSignRequired: true, clientSignRequired: false };
    case 'save-sign-send':
      return { providerSignRequired: true, clientSignRequired: false };
    case 'save-sign-request-client-signature':
      return { providerSignRequired: true, clientSignRequired: true };
    case 'save-send-request-client-signature':
      return { providerSignRequired: false, clientSignRequired: true };
  }
}

export function chartDocumentSaveSuccessMessage(action: ChartDocumentSaveAction): string {
  switch (action) {
    case 'save-and-sign':
      return 'The document has been signed and saved.';
    case 'save-sign-request-client-signature':
      return 'The document was signed and sent to the client for signature.';
    case SEND_FOR_CLIENT_SIGNATURE_ACTION:
      return 'The document was sent to the client for signature.';
    case 'save-sign-send':
      return 'The document was signed and sent to the client.';
    default:
      return 'The document was saved.';
  }
}

/** Save menu entries for the current template and sharing rules. */
export function filterChartDocumentSaveActions(
  questionnaire: Questionnaire | undefined,
): readonly { action: ChartDocumentSaveAction; label: string }[] {
  const isCoordinationRoi = questionnaire ? isCoordinationOfCareRoiTemplate(questionnaire) : false;
  const isClientShareRestricted = questionnaire ? isClientShareRestrictedTemplate(questionnaire) : false;
  const clientSignatureRequired = questionnaire ? hasSignatureRequired(questionnaire) : false;

  if (isCoordinationRoi) {
    return CHART_DOCUMENT_SAVE_ACTIONS.filter((entry) => entry.action === 'save');
  }

  if (isClientShareRestricted) {
    return CHART_DOCUMENT_SAVE_ACTIONS.filter((entry) => !saveActionSendsToClient(entry.action));
  }

  if (!clientSignatureRequired) {
    return CHART_DOCUMENT_SAVE_ACTIONS.filter(
      (entry) => entry.action !== 'save-sign-request-client-signature'
        && entry.action !== SEND_FOR_CLIENT_SIGNATURE_ACTION,
    );
  }

  return CHART_DOCUMENT_SAVE_ACTIONS;
}
